import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Player } from '@services/adminService';
import { GenderType } from '@types/index';
import { SPORTS } from '@utils/sports';
import { COUNTRIES } from '@utils/countries';
import { useAuthStore } from '@store/authStore';
import { brandColors } from '@config/brandTokens';
import { PublicProfileUrlField } from '@components/profile/PublicProfileUrlField';
import ParentalConsentFields, {
  emptyParentalConsent,
  parentalConsentComplete,
  type ParentalConsentState,
} from '@components/profile/ParentalConsentFields';
import ParentChildAgeAlert from '@components/profile/ParentChildAgeAlert';
import HouseholdParentsBlock, {
  PARENT_ROLE_LABEL,
  type HouseholdParent,
} from '@components/profile/HouseholdParentsBlock';
import SetChildPasswordControl from '@components/profile/SetChildPasswordControl';
import ResendChildConsentButton, {
  childConsentPending,
} from '@components/profile/ResendChildConsentButton';
import { playerPublicPath, suggestPublicSlug } from '@utils/publicProfileSlug';
import DashboardSubscriptionPanel from '@components/subscription/DashboardSubscriptionPanel';
import { clampFamilyPlanConfig } from '@/content/subscriptionCatalog';
import {
  evaluateParentChildAges,
  allowsAgeGapSubmitForAccount,
  peekAddChildNext,
  clearAddChildNext,
} from '@utils/parentalConsent';

interface PlayerProfileProps {
  player: Player;
  onUpdate?: (updatedPlayer: Player) => void;
}

type ProfileRow = {
  playerId: string;
  playerIdCode: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  country?: string | null;
  ageCategory?: string | null;
  primarySport?: string | null;
  secondarySport?: string | null;
  academyName?: string | null;
  schoolName?: string | null;
  collegeName?: string | null;
  employerName?: string | null;
  publicSlug?: string | null;
  phone?: string | null;
  email?: string | null;
  isYou: boolean;
  canEdit: boolean;
  isAccountHolder?: boolean;
  emailVerified?: boolean;
  consentVerified?: boolean;
  consentStatus?: 'verified' | 'pending';
  consentStatusLabel?: string;
  parentEmail?: string | null;
  custodyStatus?: string;
};

type EditForm = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  phone: string;
  country: string;
  primary_sport: string;
  secondary_sport: string;
  academy_name: string;
  school_name: string;
  college_name: string;
  employer_name: string;
  public_slug: string;
};

const emptyAddForm = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: GenderType.MALE,
};

const PROFILE_SECTION_TABS = [
  { id: 'profile' as const, label: 'Profile and Family', activeColor: brandColors.blue },
  { id: 'subscription' as const, label: 'Subscription', activeColor: brandColors.orange },
];

const verifyButtonSx = {
  textTransform: 'none' as const,
  fontWeight: 800,
  bgcolor: brandColors.orange,
  color: '#fff',
  border: 'none',
  '&:hover': { bgcolor: '#c44609', color: '#fff' },
  '&.Mui-disabled': { color: '#fff', bgcolor: '#f0a37a' },
};

function formatDate(dateString?: string | null) {
  if (!dateString) return 'Not specified';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function hasAccountEmail(email?: string | null) {
  const value = String(email || '').trim();
  if (!value) return false;
  return !value.toLowerCase().endsWith('@users.encycgames.invalid');
}

function ChildVerificationChip({
  consentVerified,
  consentStatus,
  consentStatusLabel,
}: {
  consentVerified?: boolean;
  consentStatus?: 'verified' | 'pending';
  consentStatusLabel?: string;
}) {
  const pending = childConsentPending({ consentVerified, consentStatus });
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={pending ? undefined : <CheckCircleIcon />}
      label={consentStatusLabel || (pending ? 'Verification pending' : 'Consent confirmed')}
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: '0.75rem',
        color: brandColors.navy,
        borderColor: pending ? '#cbd5e1' : `${brandColors.navy}66`,
        bgcolor: pending ? brandColors.ice : 'transparent',
        '& .MuiChip-icon': { color: brandColors.navy, fontSize: 16 },
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

function ChildFamilyActionsColumn({
  childPlayerId,
  childName,
  playerIdCode,
  consentVerified,
  consentStatus,
  consentStatusLabel,
  canSetPassword,
  passwordDisabledReason,
}: {
  childPlayerId?: string;
  childName?: string;
  playerIdCode?: string;
  consentVerified?: boolean;
  consentStatus?: 'verified' | 'pending';
  consentStatusLabel?: string;
  canSetPassword?: boolean;
  passwordDisabledReason?: string;
}) {
  if (!childPlayerId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Email sign-in
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75} alignItems="flex-start" sx={{ width: 'max-content', maxWidth: '100%' }}>
      <ChildVerificationChip
        consentVerified={consentVerified}
        consentStatus={consentStatus}
        consentStatusLabel={consentStatusLabel}
      />
      <Stack spacing={0.75} alignItems="flex-start">
        <ResendChildConsentButton
          playerId={childPlayerId}
          pending={childConsentPending({ consentVerified, consentStatus })}
        />
        <SetChildPasswordControl
          playerId={childPlayerId}
          playerIdCode={playerIdCode && playerIdCode !== '—' ? playerIdCode : undefined}
          childName={childName}
          disabled={!canSetPassword}
          disabledReason={!canSetPassword ? passwordDisabledReason : undefined}
        />
      </Stack>
    </Stack>
  );
}

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
}

function authHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = { Authorization: `Bearer ${token()}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

function rowFromPlayer(p: Player, opts: { isYou: boolean; canEdit: boolean }): ProfileRow {
  return {
    playerId: p.id,
    playerIdCode: (p as any).playerIdCode || p.playerIdCode || '',
    firstName: p.user?.firstName || (p as any).firstName || '',
    lastName: p.user?.lastName || (p as any).lastName || '',
    dateOfBirth: p.dateOfBirth,
    gender: p.gender,
    country: p.country,
    ageCategory: p.ageCategory,
    primarySport: p.primarySport,
    secondarySport: p.secondarySport,
    academyName: p.academyName,
    schoolName: (p as any).schoolName,
    collegeName: (p as any).collegeName,
    employerName: (p as any).employerName,
    publicSlug: (p as any).publicSlug || null,
    phone: p.phone,
    email: p.user?.email || (p as any).email,
    isYou: opts.isYou,
    canEdit: opts.canEdit,
    isAccountHolder: false,
  };
}

const PlayerProfile: React.FC<PlayerProfileProps> = ({ player: initialPlayer, onUpdate }) => {
  const { user, setUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addConsent, setAddConsent] = useState<ParentalConsentState>(emptyParentalConsent());
  const [verificationStatus, setVerificationStatus] = useState<{
    email: string;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    phone: string | null;
  } | null>(null);
  const [sendingEmailVerification, setSendingEmailVerification] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [householdParents, setHouseholdParents] = useState<HouseholdParent[]>([]);
  const [familyStatus, setFamilyStatus] = useState({
    maritalStatus: (user?.maritalStatus ?? null) as 'individual' | 'married' | null,
    parentRole: (user?.parentRole ?? null) as 'mother' | 'father' | 'guardian' | null,
    parentRoleSaved: !!user?.parentRoleSaved,
    canCreateChild: !!user?.canCreateChild,
  });

  const canAddChildren = !!user?.canAddChildren;
  const canEditSelf = user?.canEditProfile !== false;
  const isIndividual = familyStatus.maritalStatus === 'individual';
  const canCreateChild = familyStatus.canCreateChild && familyStatus.parentRoleSaved && !isIndividual;
  const savedParentRole = familyStatus.parentRoleSaved ? familyStatus.parentRole : null;
  const childrenSectionRef = useRef<HTMLDivElement>(null);

  const loadProfiles = async () => {
    try {
      const meRes = await fetch('/api/players/me', { headers: authHeaders() });
      const meText = await meRes.text();
      let meJson: any = {};
      if (!meText.trim()) {
        throw new Error(
          meRes.status >= 500
            ? 'Cannot reach server. Is the backend running on port 5000?'
            : 'Failed to load profiles',
        );
      }
      try {
        meJson = JSON.parse(meText);
      } catch {
        throw new Error('Failed to load profiles');
      }
      const next: ProfileRow[] = [];
      if (meJson.success && meJson.data) {
        const self = meJson.data;
        next.push({
          playerId: self.id,
          playerIdCode: self.playerIdCode,
          firstName: self.firstName || self.user?.firstName || '',
          lastName: self.lastName || self.user?.lastName || '',
          dateOfBirth: self.dateOfBirth,
          gender: self.gender,
          country: self.country,
          ageCategory: self.ageCategory,
          primarySport: self.primarySport,
          secondarySport: self.secondarySport,
          academyName: self.academyName,
          schoolName: self.schoolName,
          collegeName: self.collegeName,
          employerName: self.employerName,
          publicSlug: self.publicSlug || null,
          phone: self.phone,
          email: self.email || self.user?.email || user?.email,
          isYou: true,
          canEdit: self.canEditProfile !== false && canEditSelf,
          isAccountHolder:
            !self.isChildInCustody &&
            (self.canAddChildren !== false) &&
            canAddChildren &&
            hasAccountEmail(self.email || self.user?.email || user?.email),
          emailVerified: !!self.emailVerified,
        });
        (self.children || []).forEach((child: any) => {
          if (child.playerId === self.id) return;
          next.push({
            playerId: child.playerId,
            playerIdCode: child.playerIdCode,
            firstName: child.firstName,
            lastName: child.lastName,
            dateOfBirth: child.dateOfBirth,
            gender: child.gender,
            country: child.country,
            ageCategory: child.ageCategory,
            primarySport: child.primarySport,
            secondarySport: child.secondarySport,
            academyName: child.academyName,
            schoolName: child.schoolName,
            collegeName: child.collegeName,
            employerName: child.employerName,
            publicSlug: child.publicSlug || null,
            phone: child.phone,
            email: null,
            isYou: false,
            canEdit: canAddChildren && child.custodyStatus === 'active',
            isAccountHolder: false,
            consentVerified: child.consentVerified,
            consentStatus: child.consentStatus,
            consentStatusLabel: child.consentStatusLabel,
            parentEmail: child.parentEmail || null,
            custodyStatus: child.custodyStatus,
          });
        });
      } else if (initialPlayer?.id) {
        next.push(rowFromPlayer(initialPlayer, {
          isYou: true,
          canEdit: canEditSelf,
        }));
      }
      setRows(next);
      if (user && meJson.success && meJson.data) {
        const nextFamily = {
          maritalStatus: (meJson.data.maritalStatus ?? user.maritalStatus ?? null) as 'individual' | 'married' | null,
          parentRole: (meJson.data.parentRole ?? user.parentRole ?? null) as 'mother' | 'father' | 'guardian' | null,
          parentRoleSaved: !!(meJson.data.parentRoleSaved ?? user.parentRoleSaved),
          canCreateChild: !!(meJson.data.canCreateChild ?? user.canCreateChild),
        };
        setFamilyStatus(nextFamily);
        setUser({
          ...user,
          children: Array.isArray(meJson.data.children) ? meJson.data.children : user.children,
          canAddChildren: meJson.data.canAddChildren ?? user.canAddChildren,
          canCreateChild: nextFamily.canCreateChild,
          maritalStatus: nextFamily.maritalStatus,
          parentRole: nextFamily.parentRole,
          parentRoleSaved: nextFamily.parentRoleSaved,
          intendedChildCount: meJson.data.intendedChildCount ?? user.intendedChildCount,
          needsFamilyOnboarding: meJson.data.needsFamilyOnboarding ?? user.needsFamilyOnboarding,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profiles');
      if (initialPlayer?.id) {
        setRows([rowFromPlayer(initialPlayer, { isYou: true, canEdit: canEditSelf })]);
      }
    }
  };

  useEffect(() => {
    loadProfiles();
  }, [initialPlayer?.id, user?.id]);

  useEffect(() => {
    const pendingAddChild = searchParams.get('addChild') === '1' || peekAddChildNext();
    if (pendingAddChild && canAddChildren && canCreateChild) {
      setShowAdd(true);
      setAddForm(emptyAddForm);
      setAddConsent(emptyParentalConsent());
    }
  }, [searchParams, canAddChildren, canCreateChild]);

  useEffect(() => {
    const tab = String(searchParams.get('tab') || '').toLowerCase();
    const wantsChildren =
      tab === 'children' || searchParams.get('addChild') === '1' || Boolean(peekAddChildNext());
    if (!wantsChildren || tab === 'subscription') return;
    const timer = window.setTimeout(() => {
      childrenSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [searchParams, canAddChildren]);

  useEffect(() => {
    let cancelled = false;
    const loadHousehold = async () => {
      try {
        const res = await fetch('/api/household/parents', { headers: authHeaders() });
        const text = await res.text();
        if (!text.trim()) return;
        const json = JSON.parse(text);
        if (!cancelled && json.success && Array.isArray(json.data?.parents)) {
          setHouseholdParents(json.data.parents);
        }
      } catch {
        /* Family table still composes from /api/players/me */
      }
    };
    loadHousehold();
    return () => { cancelled = true; };
  }, [user?.id, familyStatus.parentRoleSaved]);

  const loadVerificationStatus = async () => {
    try {
      if (!canAddChildren || !canEditSelf) {
        setVerificationStatus(null);
        return;
      }
      const userId = user?.id;
      if (!userId) return;
      const response = await fetch(`/api/auth/verification-status/${userId}`);
      const data = await response.json();
      if (data.success) setVerificationStatus(data.data);
    } catch (err) {
      console.error('Error loading verification status:', err);
    }
  };

  useEffect(() => {
    loadVerificationStatus();
  }, [initialPlayer?.id, user?.id]);

  const startEdit = (row: ProfileRow) => {
    setEditingId(row.playerId);
    setForm({
      first_name: row.firstName || '',
      last_name: row.lastName || '',
      date_of_birth: row.dateOfBirth ? String(row.dateOfBirth).split('T')[0] : '',
      gender: row.gender || GenderType.MALE,
      phone: row.phone || '',
      country: row.country || '',
      primary_sport: row.primarySport || '',
      secondary_sport: row.secondarySport || '',
      academy_name: row.academyName || '',
      school_name: row.schoolName || '',
      college_name: row.collegeName || '',
      employer_name: row.employerName || '',
      public_slug: row.publicSlug || suggestPublicSlug(row.firstName, row.lastName) || '',
    });
  };

  const saveEdit = async () => {
    if (!editingId || !form) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/players/${editingId}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({
          firstName: form.first_name,
          lastName: form.last_name,
          dateOfBirth: form.date_of_birth,
          gender: form.gender,
          phone: form.phone,
          country: form.country,
          primarySport: form.primary_sport,
          secondarySport: form.secondary_sport,
          academyName: form.academy_name,
          schoolName: form.school_name,
          collegeName: form.college_name,
          employerName: form.employer_name,
          publicSlug: form.public_slug || undefined,
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to update profile');
      toast.success('Profile updated');
      setEditingId(null);
      setForm(null);
      await loadProfiles();
      if (editingId === initialPlayer.id && onUpdate) onUpdate({ ...initialPlayer, ...data.data });
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const addChild = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!canCreateChild || !savedParentRole) {
        throw new Error('Set your relation first, then add a child.');
      }
      const you = rows.find((r) => r.isYou);
      if (!you?.dateOfBirth) {
        throw new Error('Add your own date of birth on your player profile first so we can confirm you are at or above the digital consent age.');
      }
      if (!addForm.date_of_birth) {
        throw new Error('Child date of birth is required for digital consent.');
      }
      if (!parentalConsentComplete(addConsent)) {
        throw new Error('Complete the digital consent form to add a child.');
      }
      const ageCheck = evaluateParentChildAges({
        parentDateOfBirth: you.dateOfBirth,
        childDateOfBirth: addForm.date_of_birth,
        parentGender: you.gender,
        relationship: savedParentRole,
        country: you.country || 'India',
        accountEmails: [user?.email, you.email, verificationStatus?.email],
      });
      if (!ageCheck.canSubmit) {
        throw new Error(ageCheck.message || ageCheck.title || 'Parent and child ages conflict.');
      }
      const parentCountry = you.country || 'India';
      const res = await fetch('/api/children', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          ...addForm,
          country: parentCountry,
          parental_attestation: addConsent.parentalAttestation,
          consent_terms: addConsent.consentTerms,
          consent_data_processing: addConsent.consentData,
          consent_marketing: addConsent.consentMarketing,
        }),
      });
      const addText = await res.text();
      let json: any = {};
      if (!addText.trim()) {
        throw new Error(
          res.status >= 500
            ? 'Cannot reach server. Is the backend running on port 5000?'
            : 'Could not add child',
        );
      }
      try {
        json = JSON.parse(addText);
      } catch {
        throw new Error('Could not add child');
      }
      if (!res.ok || !json.success) throw new Error(json.message || 'Could not add child');
      const playerIdCode = json.data?.playerIdCode;
      const pending = !!json.data?.parentalConsentPending || json.code === 'PARENTAL_CONSENT_PENDING';
      const parentInbox = json.data?.parentEmail;
      toast.success(
        pending
          ? `Child Player ID ${playerIdCode || ''} created. Verification pending — confirm the email we sent${
              parentInbox ? ` to ${parentInbox}` : ' to your login'
            } before they can sign in.`
          : playerIdCode
            ? `Child Player ID ${playerIdCode} created. You can set a Player ID password on their row.`
            : json.message,
      );
      setAddForm(emptyAddForm);
      setAddConsent(emptyParentalConsent());
      setShowAdd(false);
      if (searchParams.get('addChild')) {
        searchParams.delete('addChild');
        setSearchParams(searchParams, { replace: true });
      }
      clearAddChildNext();
      await loadProfiles();
    } catch (err: any) {
      setError(err.message || 'Could not add child');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      setSendingEmailVerification(true);
      const userId = user?.id || initialPlayer.user?.id || (initialPlayer as any).userId;
      const response = await fetch('/api/auth/send-email-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (data.success) toast.success(data.message || 'Verification email sent!');
      else toast.error(data.message || 'Failed to send verification email');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send verification email');
    } finally {
      setSendingEmailVerification(false);
    }
  };

  const handleSendOTP = async (row: ProfileRow) => {
    try {
      setSendingOTP(true);
      const userId = user?.id || initialPlayer.user?.id;
      const phone = row.phone || verificationStatus?.phone;
      if (!phone) {
        toast.error('Add a phone number first');
        return;
      }
      const response = await fetch('/api/auth/send-phone-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, phone }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'OTP sent');
        setOtpDialogOpen(true);
      } else toast.error(data.message || 'Failed to send OTP');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setVerifyingOTP(true);
      const userId = user?.id || initialPlayer.user?.id;
      const you = rows.find((r) => r.isYou);
      const phone = you?.phone || verificationStatus?.phone;
      const response = await fetch('/api/auth/verify-phone-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, phone, otpCode }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Phone verified');
        setOtpDialogOpen(false);
        setOtpCode('');
        loadVerificationStatus();
      } else toast.error(data.message || 'Invalid OTP');
    } catch (err: any) {
      toast.error(err.message || 'Failed to verify OTP');
    } finally {
      setVerifyingOTP(false);
    }
  };

  const youRows = rows.filter((row) => row.isYou);
  const childRows = rows.filter((row) => !row.isYou);
  const accountEmails = [user?.email, youRows[0]?.email, verificationStatus?.email];
  const allowAgeGapSubmit = allowsAgeGapSubmitForAccount(...accountEmails);
  const addChildAgeCheck = evaluateParentChildAges({
    parentDateOfBirth: youRows[0]?.dateOfBirth,
    childDateOfBirth: addForm.date_of_birth,
    parentGender: youRows[0]?.gender,
    relationship: savedParentRole,
    country: youRows[0]?.country,
    allowAgeGapSubmit,
    accountEmails,
  });

  const Info = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
    </Box>
  );

  const renderProfileAccordion = (row: ProfileRow) => {
    const hasChildActions = !row.isYou && canAddChildren;
    return (
      <Accordion
        key={row.playerId}
        expanded={expanded === row.playerId}
        onChange={(_, open) => setExpanded(open ? row.playerId : false)}
        disableGutters
        sx={{ border: '1px solid #e2e8f0', borderRadius: '10px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container spacing={2} sx={{ width: '100%', pr: 1 }} alignItems="flex-start">
            <Grid item xs={12} sm={hasChildActions ? 'auto' : 6}>
              <Info label="Name" value={[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'} />
            </Grid>
            <Grid item xs={12} sm={hasChildActions ? 'auto' : 6}>
              <Info label="Player ID" value={row.playerIdCode || '—'} />
            </Grid>
            {hasChildActions && (
              <Grid
                item
                xs={12}
                sm="auto"
                onClick={(event) => event.stopPropagation()}
                sx={{ ml: { sm: 'auto' }, minWidth: { sm: 180 } }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Typography variant="caption" color="text.secondary">Actions</Typography>
                  <ChildFamilyActionsColumn
                    childPlayerId={row.playerId}
                    childName={[row.firstName, row.lastName].filter(Boolean).join(' ')}
                    playerIdCode={row.playerIdCode}
                    consentVerified={row.consentVerified}
                    consentStatus={row.consentStatus}
                    consentStatusLabel={row.consentStatusLabel}
                    canSetPassword={row.custodyStatus ? row.custodyStatus === 'active' : row.canEdit}
                    passwordDisabledReason="You can only set a password for a child still in your care."
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><Info label="Date of birth" value={formatDate(row.dateOfBirth)} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Gender" value={row.gender} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Country" value={row.country} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Phone" value={row.phone} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Primary sport" value={row.primarySport} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Secondary sport" value={row.secondarySport} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Academy or club" value={row.academyName} /></Grid>
            <Grid item xs={12} sm={6}><Info label="School" value={row.schoolName} /></Grid>
            <Grid item xs={12} sm={6}><Info label="College" value={row.collegeName} /></Grid>
            <Grid item xs={12} sm={6}><Info label="Employer" value={row.employerName} /></Grid>
            <Grid item xs={12}>
              <Info
                label="Public page"
                value={
                  playerPublicPath(row.publicSlug, row.playerIdCode) ? (
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={playerPublicPath(row.publicSlug, row.playerIdCode)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none', px: 0 }}
                    >
                      {`/in/${row.publicSlug || String(row.playerIdCode || '').toLowerCase()}`}
                    </Button>
                  ) : (
                    'Not ready yet'
                  )
                }
              />
              <Typography variant="caption" color="text.secondary">
                This page is already live from the Player ID. Choose a profile name if you want a shorter one.
              </Typography>
            </Grid>
            {row.isAccountHolder && hasAccountEmail(row.email) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.email}</Typography>
                  {(row.emailVerified || verificationStatus?.isEmailVerified) ? (
                    <Chip icon={<CheckCircleIcon />} label="Verified" color="success" size="small" />
                  ) : verificationStatus ? (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleSendEmailVerification}
                      disabled={sendingEmailVerification}
                      sx={verifyButtonSx}
                    >
                      {sendingEmailVerification ? 'Sending...' : 'Verify Email'}
                    </Button>
                  ) : null}
                  {(row.phone || verificationStatus?.phone) && verificationStatus && !verificationStatus.isPhoneVerified && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSendOTP(row)}
                      disabled={sendingOTP}
                      sx={verifyButtonSx}
                    >
                      {sendingOTP ? 'Sending...' : 'Verify phone'}
                    </Button>
                  )}
                </Stack>
              </Grid>
            )}
            {!row.isYou && (
              <Grid item xs={12}>
                {childConsentPending(row) ? (
                  <Alert
                    severity="warning"
                    action={
                      <ResendChildConsentButton playerId={row.playerId} pending />
                    }
                  >
                    Verification pending. Confirm the parental-consent email we sent
                    {row.parentEmail ? ` to ${row.parentEmail}` : ' to the parent who added this child'}.
                    The child cannot sign in with their Player ID until that link is confirmed.
                  </Alert>
                ) : (
                  <Alert severity="success">
                    Consent confirmed. This child can sign in with their Player ID.
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
          {row.canEdit && (
            <Button
              sx={{ mt: 2, textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy }}
              variant="contained"
              onClick={() => startEdit(row)}
            >
              Edit
            </Button>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const firstTaskAddChild = showAdd && (searchParams.get('addChild') === '1' || peekAddChildNext());
  const tabParam = String(searchParams.get('tab') || '').toLowerCase();
  const profileTab = tabParam === 'subscription' ? 'subscription' : 'profile';
  const showFamilyActions = canAddChildren;
  const showChildrenSection = canAddChildren;

  const setProfileTab = (next: 'profile' | 'subscription') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'profile') params.delete('tab');
    else params.set('tab', next);
    params.delete('addChild');
    setSearchParams(params, { replace: true });
  };

  const familyTableRows = useMemo(() => {
    type MemberRow = {
      key: string;
      name: string;
      relation: string;
      playerIdCode: string;
      childPlayerId?: string;
      custodyStatus?: string;
      consentVerified?: boolean;
      consentStatus?: 'verified' | 'pending';
      consentStatusLabel?: string;
      canSetChildPassword?: boolean;
    };
    const you = youRows[0];
    const youIds = new Set([you?.playerId, you?.playerIdCode].filter(Boolean).map(String));
    const isYouParent = (parent: HouseholdParent) =>
      !!parent.isYou
      || (!!parent.playerId && youIds.has(parent.playerId))
      || (!!parent.playerIdCode && youIds.has(parent.playerIdCode));
    const nameOf = (first?: string | null, last?: string | null, fallback?: string | null) =>
      [first, last].filter(Boolean).join(' ').trim() || fallback || '—';
    const fallbackRelation = savedParentRole
      ? PARENT_ROLE_LABEL[savedParentRole]
      : isIndividual
        ? 'Individual'
        : 'You';
    const selfParent = householdParents.find(isYouParent);
    const linked = householdParents.filter((parent) => !isYouParent(parent));
    const out: MemberRow[] = [];
    if (you || selfParent) {
      out.push({
        key: you?.playerId || selfParent?.userId || 'you',
        name: nameOf(
          you?.firstName || selfParent?.firstName,
          you?.lastName || selfParent?.lastName,
          you?.playerIdCode || selfParent?.playerIdCode,
        ),
        relation: selfParent
          ? (PARENT_ROLE_LABEL[selfParent.parentRole] || fallbackRelation)
          : fallbackRelation,
        playerIdCode: you?.playerIdCode || selfParent?.playerIdCode || '—',
      });
    }
    linked.forEach((parent) => {
      out.push({
        key: parent.userId || parent.playerId || parent.playerIdCode || `adult-${out.length}`,
        name: nameOf(parent.firstName, parent.lastName, parent.playerIdCode),
        relation: PARENT_ROLE_LABEL[parent.parentRole] || 'Adult',
        playerIdCode: parent.playerIdCode || '—',
      });
    });
    const seen = new Set(out.map((row) => row.playerIdCode).filter((id) => id && id !== '—'));
    childRows.forEach((row) => {
      if (row.playerIdCode && seen.has(row.playerIdCode)) return;
      if (you && row.playerId === you.playerId) return;
      const custodyActive = !row.custodyStatus || row.custodyStatus === 'active';
      out.push({
        key: row.playerId,
        name: nameOf(row.firstName, row.lastName, row.playerIdCode),
        relation: 'Child',
        playerIdCode: row.playerIdCode || '—',
        childPlayerId: row.playerId,
        custodyStatus: row.custodyStatus,
        consentVerified: row.consentVerified,
        consentStatus: row.consentStatus,
        consentStatusLabel: row.consentStatusLabel,
        canSetChildPassword: canAddChildren && custodyActive,
      });
    });
    return out;
  }, [householdParents, youRows, childRows, savedParentRole, isIndividual, canAddChildren]);

  const familyHint = clampFamilyPlanConfig({
    sportIds: Array.from(
      new Set(
        rows.flatMap((row) => [row.primarySport, row.secondarySport].filter(Boolean) as string[]),
      ),
    ),
    childrenCount: Math.max(1, rows.length),
    publicProfile: rows.some((row) => Boolean(row.publicSlug)),
  });

  const sectionTitleSx = {
    fontWeight: 800,
    color: brandColors.navy,
    fontSize: '1.05rem',
  } as const;

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.25, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '1.15rem', fontFamily: '"Sora", Arial, sans-serif' }}>
        <PersonIcon sx={{ fontSize: 22 }} />
        Profile
      </Typography>
      <Typography sx={{ color: brandColors.slate, mb: 1, fontSize: '0.88rem' }}>
        {canAddChildren
          ? 'Your player details, family, and yearly plan live on these tabs.'
          : 'Your player details and yearly plan live on these tabs.'}
      </Typography>

      <Tabs
        value={profileTab}
        onChange={(_, next: 'profile' | 'subscription') => setProfileTab(next)}
        aria-label="Profile sections"
        sx={{
          mb: 2,
          minHeight: 36,
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTabs-flexContainer': { gap: 0.25 },
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 36,
            minWidth: 0,
            px: 1.75,
            py: 0.5,
            fontFamily: '"Sora", Arial, sans-serif',
            fontSize: '0.82rem',
            fontWeight: 600,
            color: brandColors.slate,
            opacity: 0.78,
          },
          '& .MuiTab-root:hover': { opacity: 1 },
          '& .Mui-selected': { fontWeight: 800, opacity: '1 !important' },
          '& .MuiTabs-indicator': {
            height: 2.5,
            bgcolor: PROFILE_SECTION_TABS.find((tab) => tab.id === profileTab)?.activeColor || brandColors.blue,
          },
        }}
      >
        {PROFILE_SECTION_TABS.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            disableRipple
            sx={{
              '&.Mui-selected': { color: `${tab.activeColor} !important` },
            }}
          />
        ))}
      </Tabs>

      {error && profileTab !== 'subscription' && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {profileTab === 'subscription' && (
        <DashboardSubscriptionPanel familyHint={familyHint} country={youRows[0]?.country} />
      )}

      {profileTab === 'profile' && (
        <Stack spacing={3}>
          {!canEditSelf && (
            <Alert severity="warning">
              This is a child Player ID login. Profile edits are locked. A parent can update this after signing in with their email.
            </Alert>
          )}

          <Box>
            <Typography sx={{ ...sectionTitleSx, mb: 1.25 }}>
              Profile
            </Typography>
            <Stack spacing={1.25}>
              {youRows.map((row) => renderProfileAccordion(row))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ ...sectionTitleSx, mb: 1.25 }}>
              Family
            </Typography>
            <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 800, color: brandColors.navy }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: brandColors.navy }}>Relation</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: brandColors.navy }}>ID</TableCell>
                    {canAddChildren && (
                      <TableCell sx={{ fontWeight: 800, color: brandColors.navy, width: 180 }}>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {familyTableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canAddChildren ? 4 : 3}>
                        <Typography color="text.secondary">No family members yet.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    familyTableRows.map((member) => (
                      <TableRow key={member.key} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{member.name}</TableCell>
                        <TableCell>{member.relation}</TableCell>
                        <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                          {member.playerIdCode}
                        </TableCell>
                        {canAddChildren && (
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <ChildFamilyActionsColumn
                              childPlayerId={member.childPlayerId}
                              childName={member.name}
                              playerIdCode={member.playerIdCode}
                              consentVerified={member.consentVerified}
                              consentStatus={member.consentStatus}
                              consentStatusLabel={member.consentStatusLabel}
                              canSetPassword={member.canSetChildPassword}
                              passwordDisabledReason="You can only set a password for a child still in your care."
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {showFamilyActions && !isIndividual && (
            <Box>
              <Typography sx={{ ...sectionTitleSx, mb: 1.25 }}>
                Link family member
              </Typography>
              <HouseholdParentsBlock
                defaultMyRole={savedParentRole || undefined}
                defaultGender={youRows[0]?.gender}
                onLinked={loadProfiles}
                onParentsChange={setHouseholdParents}
                onStatusChange={(status) => {
                  setFamilyStatus((prev) => ({
                    maritalStatus: status.maritalStatus ?? prev.maritalStatus,
                    parentRole: status.myRole ?? prev.parentRole,
                    parentRoleSaved: !!status.myRoleSaved,
                    canCreateChild: !!status.canCreateChild,
                  }));
                  if (user) {
                    setUser({
                      ...user,
                      maritalStatus: status.maritalStatus ?? user.maritalStatus,
                      parentRole: status.myRole ?? user.parentRole,
                      parentRoleSaved: !!status.myRoleSaved,
                      canCreateChild: !!status.canCreateChild,
                      needsFamilyOnboarding: false,
                    });
                  }
                }}
              />
            </Box>
          )}

          {showChildrenSection && (
            <Box ref={childrenSectionRef} id="profile-family-children" sx={{ scrollMarginTop: 96 }}>
              {isIndividual && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Individual accounts do not add children.
                </Alert>
              )}
              {!isIndividual && !familyStatus.parentRoleSaved && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Set your relation above and save it before you can add a child.
                </Alert>
              )}
              {firstTaskAddChild && canCreateChild && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    First task: add your child now
                  </Typography>
                  Enter the child&apos;s details below. Your saved relation is used. Parents / guardians must be 21+.
                </Alert>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: childRows.length || showAdd ? 2 : 0 }}
              >
                <Box>
                  <Typography sx={sectionTitleSx}>
                    Children
                  </Typography>
                  {childRows.length > 0 && (
                    <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5 }}>
                      Each child signs in with their Player ID. Until you set a password, they can use the family password.
                    </Typography>
                  )}
                </Box>
                {!showAdd && canCreateChild && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setShowAdd(true);
                      setAddForm(emptyAddForm);
                      setAddConsent(emptyParentalConsent());
                      setError(null);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.orange }}
                  >
                    Add Child
                  </Button>
                )}
              </Stack>

              <Stack spacing={1.25}>
                {childRows.map((row) => renderProfileAccordion(row))}
              </Stack>

              {showAdd && canCreateChild && (
                <Box sx={{ mt: childRows.length ? 2 : 0, p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.5 }}>
                    Add a child
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adding as {savedParentRole === 'mother' ? 'Mother' : savedParentRole === 'father' ? 'Father' : 'Guardian'}.
                    Child date of birth is required for digital consent and parent–child age checks.
                    After you save, we email a confirmation link to your login. The child stays inactive until you confirm it.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="First name *" fullWidth value={addForm.first_name} onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Last name *" fullWidth value={addForm.last_name} onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Date of birth *" type="date" fullWidth InputLabelProps={{ shrink: true }} value={addForm.date_of_birth} onChange={(e) => setAddForm({ ...addForm, date_of_birth: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select label="Gender *" fullWidth value={addForm.gender} onChange={(e) => setAddForm({ ...addForm, gender: e.target.value as GenderType })}>
                        <MenuItem value={GenderType.MALE}>Male</MenuItem>
                        <MenuItem value={GenderType.FEMALE}>Female</MenuItem>
                        <MenuItem value={GenderType.OTHER}>Other</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                  <ParentChildAgeAlert
                    check={addChildAgeCheck}
                  />
                  <ParentalConsentFields
                    dateOfBirth={addForm.date_of_birth}
                    country={youRows[0]?.country}
                    childName={[addForm.first_name, addForm.last_name].filter(Boolean).join(' ')}
                    value={addConsent}
                    onChange={setAddConsent}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      disabled={
                        saving ||
                        !parentalConsentComplete(addConsent) ||
                        !addChildAgeCheck.canSubmit
                      }
                      onClick={addChild}
                      startIcon={<SaveIcon />}
                      sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy }}
                    >
                      {saving ? 'Creating...' : 'Create'}
                    </Button>
                    <Button
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        setShowAdd(false);
                        setAddForm(emptyAddForm);
                        setAddConsent(emptyParentalConsent());
                        if (searchParams.get('addChild')) {
                          searchParams.delete('addChild');
                          setSearchParams(searchParams, { replace: true });
                        }
                        clearAddChildNext();
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      )}

      <Dialog open={!!editingId && !!form} onClose={() => { setEditingId(null); setForm(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
          {form && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="First name" fullWidth value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Last name" fullWidth value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Date of birth" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Gender" fullWidth value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Country" fullWidth value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                  <MenuItem value="">Not specified</MenuItem>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Primary sport" fullWidth value={form.primary_sport} onChange={(e) => setForm({ ...form, primary_sport: e.target.value })}>
                  <MenuItem value="">None</MenuItem>
                  {SPORTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Secondary sport" fullWidth value={form.secondary_sport} onChange={(e) => setForm({ ...form, secondary_sport: e.target.value })}>
                  <MenuItem value="">None</MenuItem>
                  {SPORTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Academy or club" fullWidth value={form.academy_name} onChange={(e) => setForm({ ...form, academy_name: e.target.value })} helperText="Type the name if it is not in our directory." />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="School" fullWidth value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="College" fullWidth value={form.college_name} onChange={(e) => setForm({ ...form, college_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Employer / company"
                  fullWidth
                  value={form.employer_name}
                  onChange={(e) => setForm({ ...form, employer_name: e.target.value })}
                  helperText="Map yourself to your company public profile."
                />
              </Grid>
              <Grid item xs={12}>
                <PublicProfileUrlField
                  value={form.public_slug}
                  onChange={(public_slug) => setForm({ ...form, public_slug })}
                  exceptType="player"
                  exceptId={editingId || undefined}
                  suggestedFrom={[form.first_name, form.last_name]}
                  fallbackCode={rows.find((row) => row.playerId === editingId)?.playerIdCode}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingId(null); setForm(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={saveEdit} sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify phone</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label="OTP code"
            fullWidth
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOtpDialogOpen(false); setOtpCode(''); }}>Cancel</Button>
          <Button variant="contained" disabled={otpCode.length !== 6 || verifyingOTP} onClick={handleVerifyOTP} sx={verifyButtonSx}>
            {verifyingOTP ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerProfile;
